import { useEffect, useRef, useState } from 'react';
import pageService from '../../services/watchdog/watchdog.service';
import UrlsUtil from '../../utils/url.util';

type WatchdogPersistence = {
  filter: string,
  rawData: any[],
  summaries: any[],
  lastUpdated: string | undefined,
};

function WatchDog() {
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [persistence, setPersistence] = useState<WatchdogPersistence>({
    filter: '',
    rawData: [],
    summaries: [],
    lastUpdated: 'loading -- please wait...',
  });

  const ws = useRef(null);

  const clearFilter = () => {
    filterData(undefined);
    const filter = document.getElementById('filter') as HTMLInputElement;
    filter.value = '';
  };

  const filterData = async (e: any) => {
    let value = e?.target?.value || '';
    setPersistence(current => ({ ...current, 'filter': value }));
    await getData(value);
  };

  const forceRefresh = async () => {
    await getData(persistence.filter);
  };

  const getData = async (filter?: string) => {
    if (!isFetching) {
      setPersistenceProperty('lastUpdated', `querying data...`);
      setIsFetching(true);
      await pageService.getAll()
        .then(response => {
          const now = new Date();

          if (response.status === 200 && response.data) {

            response.data.sort((a: any, b: any) => {
              var aDate = new Date(a.created);
              var bDate = new Date(b.created);
              if (aDate > bDate) return -1;
              if (aDate < bDate) return 1;
              return 0;
            });

            let toPersist = response.data;
            if (filter) {
              const filterLowered = filter.toLowerCase();
              toPersist = toPersist.filter((item: any) => {
                return item.partitionKey.toLowerCase().indexOf(filterLowered) > -1
                  || item.rowKey.toLowerCase().indexOf(filterLowered) > -1
                  || item.currentState?.toLowerCase().indexOf(filterLowered) > -1
                  || item.timestamp.toString().toLowerCase().indexOf(filterLowered) > -1
                  || JSON.stringify(item.eTag).toLowerCase().indexOf(filterLowered) > -1
                  || item.expires?.toString().toLowerCase().indexOf(filterLowered) > -1;
              });
            }

            setPersistence(current => ({
              ...current,
              'lastUpdated': `last update: ${now.getHours()}:${`0${now.getMinutes()}`.slice(-2)}:${`0${now.getSeconds()}`.slice(-2)}`,
              'rawData': response.data,
              'summaries': toPersist
            }));
          }
        })
        .finally(() => setIsFetching(false));
    }
  };

  const handleKeyUp = (e: any) => {
    const key = (e.key || e.keyCode).toString().toLowerCase();
    if ((key === '8' || key === 'backspace') && persistence.filter.length === 0) {
      filterData(undefined);
    }
  };

  const highlightElement = (pattern: RegExp, prop: any): string => {
    return persistence.filter
      ? prop?.replace(pattern, `<span class='filter-highlighted'>$&</span>`) || prop
      : prop;
  }

  const setPersistenceProperty = (name: string, value: any) => {
    setPersistence(current => {
      const updated = { ...current, [name]: value };
      return updated;
    });
  };

  useEffect(() => {
    // @ts-ignore
    ws.current = new WebSocket(UrlsUtil.determineWebSocketUrl(window));

    // @ts-ignore
    ws.current.onopen = (event) => {
      console.log('watchdog: web socket opened.', event);
    };

    // @ts-ignore
    ws.current.onclose = (cls) => {
      console.log('watchdog: ws closed', cls);
    };

    // @ts-ignore
    ws.current.onerror = (err) => {
      console.log('watchdog: ws Error', err);
    };

    const wsCurrent = ws.current;
    return () => {
      // @ts-ignore
      wsCurrent?.close();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const tokenCheck = localStorage.getItem('token');
    if (!tokenCheck) {
      window.location.href = "/";
    }

    if (!isInitialized) {
      setIsInitialized(true);

      // @ts-ignore
      ws.current.onmessage = async (msg) => {
        console.log('watchdog: incoming message', msg);

        setTimeout(async () => {
          await getData(persistence.filter);
        }, 1000);
      };

      getData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialized, persistence]);

  return (
    <div className='data-display'>
      <div className="row" style={{ width: '100%', textAlign: 'left', marginBottom: '0.5em' }}>
        <div className='col-12'>
          <h1>Watchdog Service</h1>
          <div>
            Showing {persistence.summaries?.length || 0} of {persistence.rawData?.length || 0} &bull; <span style={{ marginTop: '0em' }}>{persistence.lastUpdated}</span>
            {
              persistence.summaries?.length > 0 &&
              <button className='btn btn-success header-refresh' onClick={() => forceRefresh()}>Force Refresh</button>
            }
          </div>
        </div>
      </div>
      {
        isInitialized && persistence.lastUpdated && (persistence.lastUpdated.startsWith('last update: ') || persistence.lastUpdated.startsWith('querying data')) &&
        <div className="row" style={{ width: '100%', textAlign: 'left', marginBottom: '0.5em' }}>
          <div className='col-12'>
            Filter: <input type="text" id="filter" onInput={(e) => filterData(e)} onKeyUp={(e) => handleKeyUp(e)} />
            {
              persistence.filter &&
              <span><button className='btn btn-link filter-clear' onClick={() => clearFilter()}>x</button></span>
            }
          </div>
        </div>
      }
      <table className='table table-striped table-hover'>
        <thead>
          <tr>
            <th>Partition Key</th>
            <th>Row Key</th>
            <th>Current State</th>
            <th>Timestamp</th>
            <th>ETag</th>
            <th>Expires</th>
          </tr>
        </thead>
        <tbody>
          {
            persistence.filter && persistence.summaries.length === 0
              ? <tr><td><strong>No matching records to display</strong></td></tr>
              : persistence.summaries.map((summary: any, i) => {
                const pattern = new RegExp(persistence.filter, 'ig');
                const updatedPartitionKey = highlightElement(pattern, summary.partitionKey);
                const updatedRowKey = highlightElement(pattern, summary.rowKey);
                const updatedTimestamp = highlightElement(pattern, summary.timestamp);
                const updatedETag = highlightElement(pattern, JSON.stringify(summary.eTag));
                const updatedExpires = highlightElement(pattern, summary.expires);
                const updatedCurrentState = highlightElement(pattern, summary.currentState);

                return <tr key={i}>
                  <td dangerouslySetInnerHTML={{ __html: updatedPartitionKey }}></td>
                  <td dangerouslySetInnerHTML={{ __html: updatedRowKey }}></td>
                  <td dangerouslySetInnerHTML={{ __html: updatedCurrentState }}></td>
                  <td dangerouslySetInnerHTML={{ __html: updatedTimestamp }}></td>
                  <td dangerouslySetInnerHTML={{ __html: updatedETag }}></td>
                  <td dangerouslySetInnerHTML={{ __html: updatedExpires }}></td>
                </tr>
              })
          }
        </tbody>
      </table>
    </div>
  );
};

export default WatchDog;